import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Slider from 'react-slick';
import cn from 'classnames';
import MediaQuery from 'react-responsive';
import { useTheme } from '../../../../theme';
import { getTotalPrice, MOBILE_MIN_WIDTH } from '../../../../utils';
import { Switch } from '@shared/ui';
import { getDifferentForReturnFlight, getDisabled, getIsAllServicesCanAddedToEntireFlight } from '../../store/selector';
import { differentForReturnFlight } from '../../store/actions';
import HeaderBaggageItem from './HeaderBaggageItem/HeaderBaggageItem';
import MobileBaggageSelector from './MobileBaggageSelector/MobileBaggageSelector';
import { getBaggageTab, getSelectedServices } from '../../store/selector';
import { setBaggageTab } from '../../store/actions';
import HeaderEquipmentItem from './HeaderEquipmentItem/HeaderEquipmentItem';
import Money from '../../../../Money';
import { getRelatedSegments, isActualBaggage } from '../../utils';
import PromoMessage from '../../../AdditionalBaggage/components/PromoMessage/PromoMessage';
import { useRenderers } from '../../../../renderProps';
import { SafeHtml } from '@shared/ui';
const sliderSettings = {
    slidesToShow: 6.5,
    slidesToScroll: 5,
    dots: false,
    speed: 500,
    infinite: false,
    arrows: false
};
const BaggageDialogHeader = props => {
    const theme = useTheme('UpgradeBaggageDialog').BaggageDialogHeader;
    const { t } = useTranslation('IncludedBaggage');
    const dispatch = useDispatch();
    const selectedServices = useSelector(getSelectedServices);
    const baggageTab = useSelector(getBaggageTab);
    const disabled = useSelector(getDisabled);
    const { MobileBaggageSelector: MobileBaggageSelectorOverride = MobileBaggageSelector } = useRenderers();
    const isDifferentForReturnFlight = useSelector(getDifferentForReturnFlight);
    const availableToEntireFlight = useSelector(getIsAllServicesCanAddedToEntireFlight);
    const segmentsCount = props.segments.length;
    const isMultipleSegments = segmentsCount > 1;
    const servicesByRfics = React.useMemo(() => {
        if (!baggageTab) {
            return [];
        }
        const servicesMap = new Map(props.services.map(services => [services[0].rfics, services]));
        const servicesByRfisc = servicesMap.get(baggageTab);
        if (!(servicesByRfisc === null || servicesByRfisc === void 0 ? void 0 : servicesByRfisc.length)) {
            return [];
        }
        if (servicesByRfisc.length === 1) {
            return servicesByRfisc;
        }
        const actualServicesMap = new Map();
        servicesByRfisc.forEach(service => {
            const allowedSegments = new Set(service.segmentIds.reduce((segments, ids) => [...segments, ...ids], []));
            const isAllSegments = allowedSegments.size === segmentsCount;
            const segmentKey = isAllSegments ? 'allSegments' : [...allowedSegments].sort().join('/');
            const key = `${service.rfics}/${segmentKey}`;
            const existService = actualServicesMap.get(key);
            if (!existService || isActualBaggage(service, existService)) {
                actualServicesMap.set(key, service);
            }
        });
        return [...actualServicesMap.values()];
    }, [props.services, segmentsCount, baggageTab]);
    const servicePrice = React.useMemo(() => {
        if (!(servicesByRfics === null || servicesByRfics === void 0 ? void 0 : servicesByRfics.length)) {
            return null;
        }
        return getTotalPrice(servicesByRfics, service => {
            if (isDifferentForReturnFlight) {
                return service.price;
            }
            const relatedSegmentsCount = getRelatedSegments(service.segmentIds, null).size;
            return Object.assign(Object.assign({}, service.price), { amount: service.price.amount * relatedSegmentsCount });
        });
    }, [servicesByRfics, isDifferentForReturnFlight]);
    const actualService = servicesByRfics[servicesByRfics.length - 1];
    const handlerClickSwitcher = useCallback(() => {
        dispatch(differentForReturnFlight());
    }, [dispatch]);
    const onChangeBaggageTab = useCallback((type) => {
        dispatch(setBaggageTab(type));
    }, [dispatch]);
    const renderCategory = useCallback((services) => {
        const actualService = services[0];
        const baggageRfics = actualService.rfics;
        const baggageType = actualService.type;
        const serviceIds = new Set(services.map(service => service.id));
        const isSelected = selectedServices.some(({ serviceId, count }) => serviceIds.has(serviceId) && count > 0);
        return (React.createElement(HeaderEquipmentItem, { key: baggageRfics, className: theme.categories__item, rfics: baggageRfics, baggageType: baggageType, isActive: baggageTab === baggageRfics, isSelected: isSelected, onClick: () => onChangeBaggageTab(baggageRfics) }));
    }, [selectedServices, baggageTab, onChangeBaggageTab]);
    const renderCategories = useCallback(() => {
        return (React.createElement(React.Fragment, null,
            React.createElement(MediaQuery, { minWidth: MOBILE_MIN_WIDTH + 1 },
                React.createElement("div", { className: theme.categories }, props.services.map(renderCategory))),
            React.createElement(MediaQuery, { maxWidth: MOBILE_MIN_WIDTH },
                React.createElement(Slider, Object.assign({ className: theme.categories }, sliderSettings), props.services.map(renderCategory))),
            React.createElement("div", { className: theme.category__wrapper },
                React.createElement("div", { className: theme.category_info },
                    React.createElement("span", { className: theme.category_title }, actualService.name),
                    React.createElement(SafeHtml, { tag: "span", className: theme.category_description, html: actualService.description })),
                React.createElement(MediaQuery, { maxWidth: MOBILE_MIN_WIDTH },
                    React.createElement(Money, { money: servicePrice, moneyClassName: theme.category__price })))));
    }, [baggageTab, selectedServices, props.services, isDifferentForReturnFlight]);
    const switchElement = (React.createElement(Switch, { classes: {
            label: theme.label
        }, checked: isDifferentForReturnFlight, label: t('Choose for each flight'), onChange: handlerClickSwitcher, disabled: disabled }));
    return (React.createElement("div", { className: cn(theme.row) },
        React.createElement(MediaQuery, { minWidth: MOBILE_MIN_WIDTH },
            baggageTab && renderCategories(),
            React.createElement("div", { className: cn(theme.info, {
                    [theme.equipment]: baggageTab
                }) },
                React.createElement(PromoMessage, { className: theme.promoMessage }),
                props.headerProps.description && (React.createElement("div", { className: theme.titleContainer },
                    React.createElement("div", { className: theme.description }, props.headerProps.description))),
                React.createElement("div", { className: theme.info_price },
                    React.createElement("div", { className: cn(theme.price_label, {
                            [theme.perFlight]: isDifferentForReturnFlight,
                            [theme.price_label_withPrice]: servicePrice
                        }) }, isDifferentForReturnFlight ? (t('Price per flight')) : (React.createElement(React.Fragment, null,
                        t('Price for all flights'),
                        servicePrice && (React.createElement("div", { className: theme.price_amount },
                            React.createElement(Money, { money: servicePrice })))))),
                    isMultipleSegments && availableToEntireFlight && switchElement)),
            !baggageTab && (React.createElement("div", { className: theme.baggage__container }, props.services.map(service => (React.createElement(HeaderBaggageItem, { services: service, key: service[0].rfics, allSegments: !isDifferentForReturnFlight })))))),
        React.createElement(MediaQuery, { maxWidth: MOBILE_MIN_WIDTH },
            baggageTab ? (renderCategories()) : (React.createElement(MobileBaggageSelectorOverride, { services: props.services, selectedBaggageId: props.mobileSelectedServiceId, setSelectedBaggageId: props.setMobileServiceId, allSegments: !isDifferentForReturnFlight, segments: props.segments })),
            React.createElement("div", { className: theme.toggle__wrapper },
                React.createElement("div", { className: cn(theme.price_label, { [theme.perFlight]: isDifferentForReturnFlight }) }, t(isDifferentForReturnFlight ? 'Price per flight' : 'Price for all flights')),
                isMultipleSegments && availableToEntireFlight && switchElement))));
};
export default BaggageDialogHeader;
